import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  #abortController = new AbortController();
  /** @type {IntersectionObserver | null} */
  #visibilityObserver = null;
  /** @type {boolean} */
  #pausedByVisibility = false;
  /** @type {WeakSet<HTMLVideoElement>} */
  #observedVideos = new WeakSet();
  /** @type {WeakSet<HTMLVideoElement>} */
  #mobileAutoplayRecoveryApplied = new WeakSet();
  /** @type {boolean} */
  #pausedByUser = false;
  /** @type {boolean} */
  #programmaticPause = false;
  /** @type {number} */
  #revealGeneration = 0;
  /** @type {boolean} */
  #deferredAutoplayQueued = false;

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.#handleMediaStartedPlaying, { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });

    const mobileGallerySlideshow =
      this.#isGalleryManagedMedia() &&
      this.closest('slideshow-slide') != null &&
      window.matchMedia('(max-width: 749px)').matches;

    if (!mobileGallerySlideshow) {
      this.#setupVisibilityObserver();
    }

    this.#setupPosterPreload();

    // For PDP/gallery autoplay media, start only when eligible and visible.
    if (
      this.hasAttribute('autoplay') &&
      this.#isGalleryManagedMedia() &&
      !mobileGallerySlideshow &&
      !this.hasAttribute('data-defer-autoplay')
    ) {
      const inlineVideo = this.querySelector(':scope > video');
      if (inlineVideo instanceof HTMLVideoElement) {
        inlineVideo.preload = 'metadata';
        inlineVideo.setAttribute('preload', 'metadata');
        inlineVideo.load();
      }
      requestAnimationFrame(() => {
        this.#startAutoplayIfEligible();
      });
    }

    // Above-the-fold content videos promoted to LCP defer their <video> (no inline media is
    // rendered server-side) so the poster image wins LCP. Load and play it after the first
    // paint, off the critical path; skipped under reduced motion so the poster simply remains.
    if (this.hasAttribute('autoplay') && this.hasAttribute('data-defer-autoplay')) {
      this.deferAutoplay();
    }

    queueMicrotask(() => this.#syncAutoplayPresentation());
    queueMicrotask(() => this.#setupVideoStateSync());
  }

  /**
   * Runs the callback when the main thread is idle, falling back to a short timeout.
   * @param {() => void} callback
   */
  #whenIdle(callback) {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(callback, { timeout: 2000 });
    } else {
      setTimeout(callback, 200);
    }
  }

  /**
   * Queues deferred autoplay after the first paint. The inline <video> was intentionally omitted
   * server-side (see snippets/video.liquid) so the poster image wins LCP.
   */
  deferAutoplay() {
    if (!this.hasAttribute('data-defer-autoplay')) return;
    if (this.#deferredAutoplayQueued) return;

    this.#deferredAutoplayQueued = true;
    requestAnimationFrame(() => {
      this.#whenIdle(() => {
        this.#deferredAutoplayQueued = false;
        this.#startDeferredAutoplay();
      });
    });
  }

  /**
   * Loads and plays the deferred autoplay video once the main thread is idle.
   */
  #startDeferredAutoplay() {
    if (!this.isConnected) return;
    // Respect reduced motion: keep the poster image (already the LCP) and never load the video.
    if (prefersReducedMotion()) return;
    if (!this.#isVisibleInViewport()) return;
    if (!this.#hasLoadedMedia()) {
      // Strip the heavy (2500px) poster off the deferred <video> before it is injected. Otherwise
      // the <video> paints that large poster as a new, late contentful element and supersedes the
      // responsive poster <img> as the LCP — which is exactly what we are trying to avoid. The
      // poster <img> stays painted underneath (see video.liquid CSS) until the video shows frames.
      const templateVideo = this.querySelector('template')?.content?.querySelector('video');
      if (templateVideo) {
        templateVideo.removeAttribute('poster');
        templateVideo.querySelector('img')?.remove();
      }
      this.loadContent(false);
    }

    // This marker only gates the initial load so subsequent visibility and gallery syncs
    // can resume already-loaded media immediately.
    if (!this.#hasLoadedMedia()) return;
    this.removeAttribute('data-defer-autoplay');
    this.playMedia();
  }

  /**
   * Autoplay markup injects video/iframe as a direct child, but the poster button stays visible until
   * `loadContent` runs (click). Match that state so CSS can hide the poster and duplicate preview image.
   */
  #syncAutoplayPresentation() {
    if (!this.hasAttribute('autoplay')) return;

    if (this.hasAttribute('data-card-hover-video') && prefersReducedMotion()) return;

    const inlineMedia = this.querySelector(':scope > video, :scope > iframe');
    if (!(inlineMedia instanceof HTMLElement)) return;

    this.#revealMediaWhenReady(inlineMedia);
    this.isPlaying = true;
    this.updatePlayPauseHint(true);
  }

  #markPlaying() {
    this.isPlaying = true;
    this.updatePlayPauseHint(true);
  }

  #markMediaReady() {
    if (!this.isConnected) return;
    if (this.hasAttribute('data-media-ready')) return;

    this.setAttribute('data-media-ready', 'true');
    this.closest('.product-media-container')?.classList.add('product-media-container--media-ready');
    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
    this.#syncGalleryVideoCover(true);
  }

  clearMediaReady() {
    this.#revealGeneration++;
    this.removeAttribute('data-media-ready');
    this.closest('.product-media-container')?.classList.remove('product-media-container--media-ready');
    this.refs.deferredMediaPlayButton?.classList.remove('deferred-media__playing');
    this.#syncGalleryVideoCover(false);
  }

  /**
   * Keep the poster image visible until the replacement media can paint.
   * This avoids a white flash while autoplay videos or iframes are still buffering.
   * @param {Element} content
   */
  #revealMediaWhenReady(content) {
    if (content instanceof HTMLVideoElement) {
      const generation = ++this.#revealGeneration;
      const reveal = () => {
        if (generation !== this.#revealGeneration) return;
        this.#markMediaReady();
      };
      const signal = this.#abortController.signal;

      if (typeof content.requestVideoFrameCallback === 'function') {
        content.requestVideoFrameCallback(() => {
          requestAnimationFrame(reveal);
        });
        content.addEventListener('playing', () => requestAnimationFrame(reveal), { once: true, signal });
        return;
      }

      if (content.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        requestAnimationFrame(reveal);
        return;
      }

      content.addEventListener('playing', reveal, { once: true, signal });
      window.setTimeout(() => {
        if (content.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) reveal();
      }, 900);
      return;
    }

    if (content instanceof HTMLIFrameElement) {
      const reveal = () => this.#markMediaReady();

      content.addEventListener('load', reveal, { once: true, signal: this.#abortController.signal });
      window.setTimeout(reveal, 1200);
      return;
    }

    this.#markMediaReady();
  }

  #prepareVideoForAutoplay(video) {
    if (!(video instanceof HTMLVideoElement)) return;

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.removeAttribute('poster');
    video.poster = '';
  }

  #syncGalleryVideoCover(showVideo) {
    if (!this.#shouldUseNativeMobileControls() || !this.#isGalleryManagedMedia()) return;

    const previewImage = this.closest('.product-media')?.querySelector('.product-media__image');
    if (!(previewImage instanceof HTMLElement)) return;

    if (showVideo) {
      previewImage.style.display = 'none';
      return;
    }

    previewImage.style.removeProperty('display');
  }

  #clearNativeVideoPoster(video) {
    if (!(video instanceof HTMLVideoElement)) return;
    if (!this.#shouldUseNativeMobileControls()) return;

    video.removeAttribute('poster');
    video.poster = '';
  }

  #playVideo(video) {
    if (!(video instanceof HTMLVideoElement)) return;

    this.#clearNativeVideoPoster(video);

    const signal = this.#abortController.signal;
    const play = () => {
      this.#markPlaying();
      video.play().catch(() => {});

      if (this.#shouldUseNativeMobileControls() && this.#isGalleryManagedMedia()) {
        this.#scheduleMobileAutoplayRecovery(video);
      }
    };

    if (this.#shouldUseNativeMobileControls()) {
      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        play();
        return;
      }

      video.addEventListener('canplay', play, { once: true, signal });
      return;
    }

    if (video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) {
      video.addEventListener('loadeddata', play, { once: true, signal });
      video.addEventListener('canplay', play, { once: true, signal });
    }

    play();
  }

  #setupPosterPreload() {
    const posterButton = this.refs.deferredMediaPlayButton;
    if (!(posterButton instanceof HTMLElement)) return;

    const signal = this.#abortController.signal;
    posterButton.addEventListener('pointerenter', this.#preloadNativeVideo, { signal });
    posterButton.addEventListener('focus', this.#preloadNativeVideo, { signal });
    posterButton.addEventListener('touchstart', this.#preloadNativeVideo, { passive: true, signal });
  }

  #preloadNativeVideo = () => {
    if (this.getAttribute('data-media-loaded') || this.#hasLoadedMedia()) return;

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);
    if (!(content instanceof HTMLVideoElement)) return;

    content.autoplay = false;
    content.removeAttribute('autoplay');
    const isCardHoverVideo = this.hasAttribute('data-card-hover-video');
    content.preload = isCardHoverVideo ? 'none' : 'metadata';
    content.setAttribute('preload', content.preload);
    this.#prepareVideoForAutoplay(content);

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);
    this.#setupVideoStateSync();
    this.#applyNativeMobileVideoControls();
  };

  /**
   * iOS/Safari can report autoplay progress while the first frame is still not painted.
   * If we detect that state on mobile, do a one-time restart from 0 to force frame rendering.
   * @param {HTMLVideoElement} video
   */
  #scheduleMobileAutoplayRecovery(video) {
    if (!(video instanceof HTMLVideoElement)) return;
    if (!this.hasAttribute('autoplay')) return;
    if (!this.#shouldUseNativeMobileControls() || !this.#isGalleryManagedMedia()) return;
    if (this.#mobileAutoplayRecoveryApplied.has(video)) return;

    let hasRenderedFrame = false;

    if (typeof video.requestVideoFrameCallback === 'function') {
      video.requestVideoFrameCallback(() => {
        hasRenderedFrame = true;
      });
    }

    window.setTimeout(() => {
      if (this.#abortController.signal.aborted) return;
      if (!this.isConnected || !video.isConnected) return;
      if (hasRenderedFrame) return;
      if (video.paused || video.currentTime <= 0) return;
      if (this.#pausedByUser) return;

      this.#mobileAutoplayRecoveryApplied.add(video);

      this.#programmaticPause = true;
      video.pause();
      try {
        video.currentTime = 0;
      } catch {
        this.#programmaticPause = false;
        return;
      }

      requestAnimationFrame(() => {
        this.#programmaticPause = false;
        if (this.#abortController.signal.aborted) return;
        if (!this.isConnected || !video.isConnected) return;
        if (this.#pausedByUser) return;
        this.#playVideo(video);
      });
    }, 320);
  }

  #shouldUseNativeMobileControls() {
    return window.matchMedia('(max-width: 749px)').matches && this.closest('.product-media') != null;
  }

  #applyNativeMobileVideoControls() {
    if (!this.#shouldUseNativeMobileControls()) return;

    const video = this.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;

    video.controls = true;
    video.setAttribute('controls', '');
  }

  #setupVideoStateSync() {
    const video = this.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;
    if (this.#observedVideos.has(video)) return;

    const signal = this.#abortController.signal;
    video.addEventListener('play', this.#handleNativeVideoPlay, { signal });
    video.addEventListener('pause', this.#handleNativeVideoPause, { signal });
    video.addEventListener('ended', this.#handleNativeVideoPause, { signal });
    video.addEventListener('loadeddata', this.#handleNativeVideoReady, { signal });
    video.addEventListener('canplay', this.#handleNativeVideoReady, { signal });
    video.addEventListener('playing', this.#handleNativeVideoReady, { signal });
    video.addEventListener('waiting', this.#handleNativeVideoWaiting, { signal });
    this.#observedVideos.add(video);
  }

  #schedulePriorityPosterAutoplay() {
    const signal = this.#abortController.signal;
    let hasStarted = false;

    const start = () => {
      if (hasStarted) return;
      if (signal.aborted) return;
      if (!this.isConnected || !this.#isVisibleInViewport()) return;
      hasStarted = true;
      this.loadContent(false);
    };

    const scheduleAfterLoad = () => {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(start, { timeout: 2500 });
      } else {
        window.setTimeout(start, 1500);
      }
    };

    if (document.readyState === 'complete') {
      scheduleAfterLoad();
    } else {
      window.addEventListener('load', scheduleAfterLoad, { once: true, signal });
    }

    for (const eventName of ['pointerdown', 'keydown', 'touchstart']) {
      window.addEventListener(eventName, start, { once: true, passive: true, signal });
    }
  }

  /**
   * Keeps a priority LCP poster visible until the deferred video has a frame ready to paint.
   * @param {Element} content
   */
  #setupPriorityPosterHandoff(content) {
    if (!this.hasAttribute('data-priority-poster')) return;

    const markReady = () => {
      this.setAttribute('data-priority-poster-ready', 'true');
    };

    const signal = this.#abortController.signal;
    if (content instanceof HTMLVideoElement) {
      if (content.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        markReady();
        return;
      }

      content.addEventListener('loadeddata', markReady, { once: true, signal });
      content.addEventListener('canplay', markReady, { once: true, signal });
      content.addEventListener('playing', markReady, { once: true, signal });
      return;
    }

    if (content instanceof HTMLIFrameElement) {
      content.addEventListener('load', markReady, { once: true, signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    const hideToggleButton = this.hasAttribute('data-hide-toggle-media-button');
    if (toggleMediaButton instanceof HTMLElement) {
      if (this.#shouldUseNativeMobileControls()) {
        toggleMediaButton.classList.add('hidden');
        return;
      }

      if (hideToggleButton) {
        toggleMediaButton.classList.add('hidden');
        return;
      }

      toggleMediaButton.classList.remove('hidden');
      toggleMediaButton.classList.toggle('is-paused', !isPlaying);
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    if (this.getAttribute('data-media-loaded')) {
      const loadedMedia = this.querySelector(':scope > video, :scope > iframe');
      if (loadedMedia instanceof Element) {
        this.#revealMediaWhenReady(loadedMedia);
      }
      this.playMedia();
      return;
    }

    this.loadContent(true);
  };

  prepareContent() {
    if (this.getAttribute('data-media-loaded')) return;

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);
    this.#setupVideoStateSync();
    this.#applyNativeMobileVideoControls();

    const video = content instanceof HTMLVideoElement ? content : this.querySelector('video');
    if (video instanceof HTMLVideoElement) {
      const isCardHoverVideo = this.hasAttribute('data-card-hover-video');
      video.preload = isCardHoverVideo ? 'none' : 'metadata';
      video.setAttribute('preload', video.preload);
      this.#prepareVideoForAutoplay(video);
      video.load();
    }
  }

  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    const slideshow = this.closest('slideshow-component');
    slideshow?.setAttribute('data-sync-lock', '');

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) {
      slideshow?.removeAttribute('data-sync-lock');
      return;
    }

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);
    this.#setupVideoStateSync();
    this.#setupPriorityPosterHandoff(content);
    this.#applyNativeMobileVideoControls();

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    if (content instanceof Element) {
      this.#revealMediaWhenReady(content);
    }

    if (content instanceof HTMLVideoElement && this.hasAttribute('autoplay')) {
      const isCardHoverVideo = this.hasAttribute('data-card-hover-video');
      content.preload = isCardHoverVideo ? 'none' : 'metadata';
      content.setAttribute('preload', content.preload);
      this.#prepareVideoForAutoplay(content);
      content.load();
      this.#playVideo(content);
    } else if (content instanceof HTMLVideoElement && focus) {
      this.#playVideo(content);
    }

    if (content instanceof HTMLIFrameElement && (this.hasAttribute('autoplay') || focus)) {
      this.#markPlaying();
    }

    requestAnimationFrame(() => {
      slideshow?.removeAttribute('data-sync-lock');
    });
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    if (this.#pausedByUser) return;

    this.#pausedByVisibility = false;
    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
      this.#markPlaying();
    } else {
      const video = this.querySelector('video');
      if (video instanceof HTMLVideoElement && this.hasAttribute('autoplay')) {
        this.#prepareVideoForAutoplay(video);
      }
      if (video instanceof HTMLVideoElement) {
        this.#revealMediaWhenReady(video);
        this.#playVideo(video);
      }

      if (!(video instanceof HTMLVideoElement) || !this.#shouldUseNativeMobileControls()) {
        this.#markPlaying();
      }
    }
    this.#applyNativeMobileVideoControls();
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      const video = this.querySelector('video');
      if (video instanceof HTMLVideoElement) {
        this.#programmaticPause = true;
        video.pause();
        window.setTimeout(() => {
          this.#programmaticPause = false;
        }, 120);
      }
    }
    this.isPlaying = false;
    this.#syncGalleryVideoCover(false);

    // Inline autoplay videos render media nodes immediately but may not set
    // data-media-loaded; only update hints when media is actually present.
    if (!this.getAttribute('data-media-loaded') && !this.#hasLoadedMedia()) return;

    this.updatePlayPauseHint(this.isPlaying);
  }

  #handleMediaStartedPlaying = (event) => {
    const source = event.detail.resource;
    if (source === this) return;
    if (!(source instanceof HTMLElement)) return;
    if (!this.#sharesPlaybackScopeWith(source)) return;

    const thisSlide = this.closest('slideshow-slide');
    const sourceSlide = source.closest('slideshow-slide');
    if (thisSlide instanceof HTMLElement && sourceSlide instanceof HTMLElement) {
      const thisIsActive = thisSlide.getAttribute('aria-hidden') === 'false';
      const sourceIsActive = sourceSlide.getAttribute('aria-hidden') === 'false';

      // Ignore hidden slide media trying to steal playback from the active slide.
      if (thisIsActive && !sourceIsActive) {
        return;
      }
    }
    if (thisSlide instanceof HTMLElement && sourceSlide == null) {
      const thisIsActive = thisSlide.getAttribute('aria-hidden') === 'false';
      // Ignore non-slideshow media trying to steal playback from the active slide.
      if (thisIsActive) {
        return;
      }
    }

    this.pauseMedia();
  };

  #handleNativeVideoPlay = (event) => {
    this.#pausedByUser = false;
    this.dispatchEvent(new MediaStartedPlayingEvent(this));
    if (event.currentTarget instanceof HTMLVideoElement) {
      this.#clearNativeVideoPoster(event.currentTarget);
    }
    if (this.#shouldUseNativeMobileControls() && !this.hasAttribute('autoplay')) {
      this.#markMediaReady();
    }
    this.#handleNativeVideoReady(event);
    this.isPlaying = true;
    this.updatePlayPauseHint(true);
  };

  #handleNativeVideoPause = () => {
    if (!this.#programmaticPause && this.#shouldUseNativeMobileControls()) {
      this.#pausedByUser = true;
    }

    this.isPlaying = false;
    this.updatePlayPauseHint(false);
  };

  #handleNativeVideoReady = (event) => {
    const video = event.currentTarget;
    if (!(video instanceof HTMLVideoElement)) return;
    if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA && video.currentTime <= 0) return;
    if (!this.#shouldUseNativeMobileControls()) return;
    if (this.hasAttribute('autoplay')) return;

    this.#markMediaReady();
  };

  #handleNativeVideoWaiting = (event) => {
    if (!this.hasAttribute('autoplay')) return;

    const video = event.currentTarget;
    if (!(video instanceof HTMLVideoElement)) return;
    if (!this.hasAttribute('data-media-ready')) return;
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) return;

    this.clearMediaReady();
    this.#revealMediaWhenReady(video);
  };

  #setupVisibilityObserver() {
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;

        if (!entry.isIntersecting) {
          if (this.#isActiveSlideshowSlideMedia()) return;

          if (this.isPlaying) {
            this.#pausedByVisibility = true;
          }
          this.pauseMedia();
          return;
        }

        if (!this.hasAttribute('autoplay')) return;
        if (this.isPlaying) return;
        if (this.hasAttribute('data-defer-autoplay')) {
          this.deferAutoplay();
          return;
        }
        this.#startAutoplayIfEligible();
      },
      {
        threshold: 0.15,
      }
    );
    this.#visibilityObserver.observe(this);
  }

  #hasLoadedMedia() {
    return this.querySelector('video, iframe[data-video-type]') != null;
  }

  #isPrimaryAutoplayMedia() {
    const scope = this.closest('media-gallery') || this.closest('.shopify-section, dialog') || document;

    const activeSlideshowAutoplayMedia = scope.querySelector(
      'slideshow-slide[aria-hidden="false"] deferred-media[autoplay]'
    );
    if (activeSlideshowAutoplayMedia instanceof HTMLElement) {
      return activeSlideshowAutoplayMedia === this;
    }

    const autoplayMedia = Array.from(scope.querySelectorAll('deferred-media[autoplay]'));
    if (!autoplayMedia.length) return true;

    const firstVisibleAutoplayMedia =
      autoplayMedia.find((media) => this.#isElementVisibleInViewport(media)) || autoplayMedia[0];

    return firstVisibleAutoplayMedia === this;
  }

  #isVisibleInViewport() {
    return this.#isElementVisibleInViewport(this);
  }

  #isElementVisibleInViewport(element) {
    const rect = element.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

    const visibleWidth = Math.max(0, Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0));
    const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
    const visibleArea = visibleWidth * visibleHeight;
    const totalArea = rect.width * rect.height;

    if (!totalArea) return false;

    return visibleArea / totalArea >= 0.15;
  }

  #isGalleryManagedMedia() {
    return this.closest('media-gallery') != null;
  }

  #isActiveSlideshowSlideMedia() {
    const slide = this.closest('slideshow-slide');
    if (!(slide instanceof HTMLElement)) return false;

    return slide.getAttribute('aria-hidden') === 'false';
  }

  #sharesPlaybackScopeWith(element) {
    return this.#getPlaybackScope(this) === this.#getPlaybackScope(element);
  }

  #getPlaybackScope(element) {
    return (
      element.closest('.media-gallery__grid') ||
      element.closest('slideshow-component') ||
      element.closest('media-gallery') ||
      element.closest('dialog') ||
      element.closest('.shopify-section') ||
      document
    );
  }

  #startAutoplayIfEligible() {
    if (!this.isConnected) return;
    // Product-card hover videos must wait for the card interaction. Their visibility
    // observer is for pausing loaded media, not for starting it before hover.
    if (this.hasAttribute('data-card-hover-video')) return;
    if (this.#pausedByUser) return;

    if (this.closest('slideshow-slide') && !this.#isActiveSlideshowSlideMedia()) {
      this.pauseMedia();
      return;
    }

    if (!this.#isPrimaryAutoplayMedia()) {
      this.pauseMedia();
      return;
    }

    if (!this.#isVisibleInViewport()) {
      this.pauseMedia();
      return;
    }

    if (this.hasAttribute('data-defer-autoplay')) {
      this.deferAutoplay();
      return;
    }

    if (!this.#hasLoadedMedia()) {
      this.loadContent(false);
    }

    this.playMedia();
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
